import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, CheckCircle, FileText, Scale, Receipt,
  Calendar, Clock,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import NewsletterSignup from "@/components/NewsletterSignup";

/* eslint-disable react/no-unescaped-entities */

// French-Canadian landing page for the Quebec landlord market.
// Mirrors /landlord-software/quebec (English) and is cross-linked via
// rel="alternate" hreflang on both pages so Google serves the right
// locale and the SERP shows a French-language result for fr-CA queries.

const PAINS = [
  "Délais d'attente au TAL (Tribunal administratif du logement) de 12 à 24 mois pour les causes de non-paiement.",
  "Formulaire F (bail obligatoire) en français — les règles de sous-location et de cession diffèrent de toutes les autres provinces.",
  "Fixation du loyer : les locataires peuvent contester toute augmentation devant le TAL, ce qui se solde souvent par une hausse de 5 à 10 $/mois face à votre demande de 50 $.",
];

const FEATURES = [
  { icon: FileText, t: "Générateur de Formulaire F (bail)", d: "Bail bilingue français + anglais avec la Section G obligatoire (« loyer payé au cours des 12 derniers mois ») préremplie." },
  { icon: Scale, t: "Préparateur de dossier TAL", d: "Demande et pièces justificatives formatées pour téléversement direct sur le portail e-services du TAL." },
  { icon: Receipt, t: "Calculateur de fixation de loyer", d: "Méthodologie officielle du TAL — frais d'exploitation, taxes, améliorations majeures — pour estimer l'augmentation qui résistera à une contestation." },
];

const CITIES = ["Montréal", "Québec", "Laval", "Gatineau", "Longueuil", "Sherbrooke", "Saguenay"];

const FAQS = [
  { q: "Comment se calcule la hausse de loyer au Québec ?", a: "Le TAL publie chaque janvier une grille de calcul officielle. Central Rentals applique cette grille et calcule automatiquement l'augmentation que vous pouvez justifier en cas de contestation." },
  { q: "Le Formulaire F (bail du TAL) est-il obligatoire ?", a: "Oui — le Formulaire F émis par le TAL est le seul bail résidentiel valide au Québec. Les ententes parallèles sont inapplicables à moins d'être annexées au Formulaire F." },
  { q: "Combien de temps prend le TAL pour traiter un dossier de non-paiement ?", a: "L'objectif publié par le TAL est de 1,5 mois pour le non-paiement. Dans les faits, en 2024-2025, la moyenne se situe entre 12 et 24 mois. Central Rentals suit chaque dossier pour démontrer les arriérés cumulés à l'audience." },
  { q: "Puis-je essayer Central Rentals gratuitement ?", a: "Oui — l'essai gratuit de 30 jours est offert sur les trois plans. Une carte de crédit est demandée pour confirmer votre identité, mais aucun montant n'est facturé pendant l'essai et vous pouvez annuler en un clic." },
];

const URL_FR = "https://centralrentals.ca/fr/landlord-software/quebec";
const URL_EN = "https://centralrentals.ca/landlord-software/quebec";

export default function QuebecFr() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${window.location.origin}/api/blog/posts`);
        const all = r.ok ? await r.json() : [];
        const filtered = (Array.isArray(all) ? all : [])
          .filter((p) => p.category === "Quebec")
          .slice(0, 6);
        if (!cancelled) setPosts(filtered);
      } catch { if (!cancelled) setPosts([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Per-page SEO meta + JSON-LD + hreflang alternates pointing both
  // directions between the EN and FR Quebec pages.
  useEffect(() => {
    const title = "Logiciel de gestion immobilière pour propriétaires québécois · Central Rentals";
    const desc = "Conçu pour les propriétaires du Québec. Bâti autour du TAL, du Formulaire F et des règles uniques de fixation des loyers. Essai gratuit de 30 jours — sans carte requise.";
    document.title = title;
    const prevLang = document.documentElement.lang;
    document.documentElement.lang = "fr-CA";

    const created = [];
    const setTag = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      let owned = false;
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
        owned = true;
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      if (owned) created.push(el);
      return el;
    };

    // Standard meta
    setTag('meta[name="description"]', { name: "description", content: desc });
    setTag('link[rel="canonical"]', { rel: "canonical", href: URL_FR });
    setTag('meta[property="og:type"]', { property: "og:type", content: "website" });
    setTag('meta[property="og:title"]', { property: "og:title", content: title });
    setTag('meta[property="og:description"]', { property: "og:description", content: desc });
    setTag('meta[property="og:url"]', { property: "og:url", content: URL_FR });
    setTag('meta[property="og:locale"]', { property: "og:locale", content: "fr_CA" });
    setTag('meta[property="og:locale:alternate"]', { property: "og:locale:alternate", content: "en_CA" });

    // hreflang alternates — three entries per Google's spec
    // (current locale + en-CA counterpart + x-default).
    const alt = (hreflang, href) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      link.setAttribute("href", href);
      link.setAttribute("data-hreflang-qc", "1");
      document.head.appendChild(link);
      created.push(link);
    };
    alt("fr-CA", URL_FR);
    alt("en-CA", URL_EN);
    alt("x-default", URL_EN);

    // FAQ + Service JSON-LD in French.
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "Central Rentals — Logiciel de gestion immobilière pour le Québec",
          "serviceType": "Logiciel de gestion immobilière",
          "provider": { "@type": "Organization", "name": "Central Rentals Canada", "url": "https://centralrentals.ca/" },
          "areaServed": { "@type": "AdministrativeArea", "name": "Québec", "addressCountry": "CA" },
          "url": URL_FR,
          "inLanguage": "fr-CA",
          "description": desc,
        },
        {
          "@type": "FAQPage",
          "inLanguage": "fr-CA",
          "mainEntity": FAQS.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        },
      ],
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-quebec-fr-jsonld", "1");
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);
    created.push(script);

    return () => {
      document.documentElement.lang = prevLang || "en-CA";
      created.forEach((el) => el && el.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="province-quebec-fr" lang="fr-CA">
      <PublicHeader light={true}/>

      {/* Language switcher */}
      <div className="bg-emerald-50 border-b border-emerald-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-2 text-sm flex items-center justify-end gap-3 text-emerald-900">
          <span className="font-semibold">FR · Québec</span>
          <span className="text-emerald-300">|</span>
          <Link to="/landlord-software/quebec" data-testid="quebec-fr-en-switch" className="hover:underline">
            Read in English
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-10 pb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700 mb-3">
          QC · Logiciel de gestion immobilière
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[1.02] text-slate-900">
          Conçu pour les propriétaires <span className="text-emerald-700">québécois</span>.
        </h1>
        <p className="mt-5 text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed">
          Bâti autour du TAL, du Formulaire F obligatoire et des règles uniques de fixation des loyers du Québec.
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link to="/signup?role=landlord&plan=professional" data-testid="quebec-fr-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm">
            Essayer gratuitement 30 jours <ArrowRight className="w-4 h-4"/>
          </Link>
          <Link to="/blog" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-200 hover:border-emerald-700 text-slate-800 font-semibold">
            Lire nos articles québécois
          </Link>
        </div>
      </section>

      {/* Pain points */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-4">
          Ce qui fait perdre du temps aux propriétaires québécois
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {PAINS.map((p, i) => (
            <div key={i} className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="text-2xl font-display font-bold text-slate-300">{(i+1).toString().padStart(2, "0")}</div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 max-w-3xl">
            Ce que nous livrons spécifiquement pour le Québec.
          </h2>
          <div className="mt-9 grid md:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="p-6 rounded-2xl bg-white border border-slate-200">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5"/>
                  </div>
                  <h3 className="font-display text-lg font-bold text-slate-900">{f.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-12">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-4">Utilisé par des propriétaires partout au Québec</div>
        <div className="flex flex-wrap gap-2">
          {CITIES.map((c) => (
            <div key={c} className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-700 font-medium">{c}</div>
          ))}
        </div>
      </section>

      {/* Blog feed */}
      {posts.length > 0 && (
        <section className="bg-slate-50 border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
            <div className="flex items-end justify-between gap-4 mb-7">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Notes du terrain</div>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 mt-1">
                  Dernières lectures pour le Québec
                </h2>
              </div>
              <Link to="/blog" className="text-sm font-semibold text-emerald-700 hover:underline whitespace-nowrap">Tous les articles →</Link>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block rounded-2xl border border-slate-200 bg-white hover:border-emerald-200 hover:shadow-md transition-all overflow-hidden">
                  {p.hero_image && (
                    <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                      <img src={p.hero_image} alt={p.hero_alt || p.title} loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">{p.category}</div>
                    <h3 className="font-display mt-2 text-base font-bold leading-snug text-slate-900 group-hover:text-emerald-800">{p.title}</h3>
                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/>{p.published_at ? new Date(p.published_at).toLocaleDateString("fr-CA", {month: "short", day: "numeric", year: "numeric"}) : ""}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{p.read_mins} min</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-14">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Questions fréquentes</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 mt-2 mb-6">
          Questions des propriétaires québécois
        </h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group p-5 rounded-xl border border-slate-200 hover:border-emerald-300 transition-colors">
              <summary className="font-semibold text-slate-900 cursor-pointer list-none flex items-start gap-3">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-1 flex-none"/>
                <span className="flex-1">{f.q}</span>
              </summary>
              <p className="mt-3 ml-7 text-sm text-slate-600 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900">
            Prêt à consacrer moins de temps à la paperasse québécoise ?
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">30 jours gratuits. Aucune carte requise. Annulation en un clic.</p>
          <Link to="/signup?role=landlord&plan=professional"
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm">
            Commencer l'essai gratuit <ArrowRight className="w-4 h-4"/>
          </Link>
          <div className="mt-10 max-w-md mx-auto text-left">
            <NewsletterSignup source="province-quebec-fr" compact />
          </div>
        </div>
      </section>

    </div>
  );
}
